import { z } from "zod"

export const journeyTypeSchema = z.enum(["guided", "taste", "browse"])
export const journeyConfigSchema = z
	.object({
		id: z.string().regex(/^[a-z0-9-]{1,60}$/),
		type: journeyTypeSchema,
		enabled: z.boolean(),
		label: z.string().trim().min(1).max(80),
		introduction: z.string().trim().max(500),
		prompt: z.string().trim().max(500),
		query: z.string().trim().max(200),
		category: z.string().trim().max(100),
		maxPrice: z.number().nonnegative().nullable(),
		productIds: z.array(z.string().regex(/^\d+$/)).max(100),
		rounds: z.union([z.literal(6), z.literal(8), z.literal(10)]),
	})
	.strict()
export const journeySettingsSchema = z
	.object({
		welcome: z.string().trim().min(1).max(500),
		journeys: z.array(journeyConfigSchema).max(30),
	})
	.strict()
	.superRefine((value, ctx) => {
		if (new Set(value.journeys.map((j) => j.id)).size !== value.journeys.length)
			ctx.addIssue({ code: z.ZodIssueCode.custom, message: "Journey identifiers must be unique" })
	})
export type JourneyConfig = z.infer<typeof journeyConfigSchema>
export type JourneySettings = z.infer<typeof journeySettingsSchema>
export function defaultJourneySettings(): JourneySettings {
	return {
		welcome: "Welcome. How would you like to shop?",
		journeys: [
			{
				id: "find",
				type: "guided",
				enabled: true,
				label: "Find something for me",
				introduction: "Tell us what you have in mind.",
				prompt: "What is the occasion, style or budget?",
				query: "",
				category: "",
				maxPrice: null,
				productIds: [],
				rounds: 6,
			},
			{
				id: "taste",
				type: "taste",
				enabled: true,
				label: "Discover my taste",
				introduction: "Choose the piece you prefer. Skip whenever neither fits.",
				prompt: "Which would you choose?",
				query: "*",
				category: "",
				maxPrice: null,
				productIds: [],
				rounds: 6,
			},
			{
				id: "browse",
				type: "browse",
				enabled: true,
				label: "Browse the collection",
				introduction: "Explore the collection and save your favourites.",
				prompt: "What would you like to see?",
				query: "*",
				category: "",
				maxPrice: null,
				productIds: [],
				rounds: 6,
			},
		],
	}
}

// Additional entry experiences reuse the supported guided flow and remain opt-in per brand.
export function journeyTemplates(): JourneyConfig[] {
	const defaults = defaultJourneySettings().journeys
	const guided = defaults[0]
	return [
		...defaults,
		{
			...guided,
			id: "occasion",
			label: "Shop for an occasion",
			introduction: "Find pieces for the moment you have in mind.",
			prompt: "What is the occasion, and what style or budget would suit it?",
		},
		{
			...guided,
			id: "budget",
			label: "Find something within my budget",
			introduction: "Explore pieces within a comfortable budget.",
			prompt: "What are you shopping for, and what is your maximum budget?",
		},
	]
}
